import axios from 'axios';

/**
 * API Service Layer
 * طبقة الاتصال بالـ Backend
 *
 * Protocols Applied (From Father DB - 138 protocols loaded):
 * - P-API-001: RESTful API Structure
 * - P010: Error Handling
 * - P-AUTH-001: JWT Token Management
 * - P-NETWORK-001: Request/Response Interceptors
 * - P-CACHE-001: Offline Cache Strategy
 */

// Get API base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:4030/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

/**
 * Request Interceptor - إضافة JWT Token
 */
api.interceptors.request.use(
  (config) => {
    // Get JWT token from localStorage (must match storage.js key)
    const token = localStorage.getItem('delivery_access_token');

    console.log('🔵 [API Interceptor] Adding token to request:', !!token);
    if (token) {
      console.log('🔵 [API Interceptor] Token:', token.substring(0, 20) + '...');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response Interceptor - معالجة الأخطاء
 */
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle different error types
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;

      switch (status) {
        case 401:
          // Unauthorized - clear ALL auth data and redirect to login
          console.log('❌ [API 401] Unauthorized - Clearing auth data and redirecting to login');

          // Clear all authentication data
          localStorage.removeItem('delivery_access_token');
          localStorage.removeItem('delivery_refresh_token');
          localStorage.removeItem('delivery-auth-storage'); // Clear Zustand persisted state
          localStorage.removeItem('delivery_user_data');

          // Prevent infinite loop - only redirect if not already on login page
          if (!window.location.pathname.includes('/login')) {
            console.log('❌ [API 401] Redirecting to /login');
            // Use replace to prevent back button issues
            window.location.replace('/login');
          }
          break;
        case 403:
          // Forbidden
          console.error('Access denied:', error.response.data.message);
          break;
        case 404:
          // Not found
          console.error('Resource not found:', error.response.data.message);
          break;
        case 422:
          // Validation error
          console.error('Validation error:', error.response.data.errors);
          break;
        case 500:
          // Server error
          console.error('Server error:', error.response.data.message);
          break;
        default:
          console.error('API error:', error.response.data.message);
      }
    } else if (error.request) {
      // Request made but no response received (network error)
      console.error('Network error - no response received');
    } else {
      // Something else happened
      console.error('Request error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * Products API - Using my-inventory endpoint for representative's vehicle products
 */
export const productsAPI = {
  getAll: () => api.get('/mobile/my-inventory'), // Fixed: Use my-inventory to get rep's loaded products
  getById: (id) => api.get(`/mobile/products/${id}`),
  create: (data) => api.post('/mobile/products', data),
  update: (id, data) => api.put(`/mobile/products/${id}`, data),
  delete: (id) => api.delete(`/mobile/products/${id}`),
  search: (query) => api.get(`/mobile/products/search?query=${encodeURIComponent(query)}`)
};

/**
 * Inventory Report API
 */
export const inventoryAPI = {
  getStatistics: () => api.get('/mobile/inventory/statistics'),
  getByStatus: (status) => api.get(`/mobile/inventory/status/${status}`),
  getLowStock: () => api.get('/mobile/inventory/low-stock'),
  getOutOfStock: () => api.get('/mobile/inventory/out-of-stock'),
  getSlowMoving: () => api.get('/mobile/inventory/slow-moving'),
  getFastMoving: (limit = 10) => api.get(`/mobile/inventory/fast-moving?limit=${limit}`),
  getValue: () => api.get('/mobile/inventory/value')
};

/**
 * Customers API
 */
export const customersAPI = {
  getAll: () => api.get('/mobile/customers'),
  getById: (id) => api.get(`/mobile/customers/${id}`),
  create: (data) => api.post('/mobile/customers', data),
  update: (id, data) => api.put(`/mobile/customers/${id}`, data),
  delete: (id) => api.delete(`/mobile/customers/${id}`),
  search: (query) => api.get(`/mobile/customers/search?query=${encodeURIComponent(query)}`),
  getPaymentHistory: (id) => api.get(`/mobile/customers/${id}/payments`),
  // New Report Endpoints
  getOverdue: () => api.get('/mobile/customers/overdue'),
  getStatistics: () => api.get('/mobile/customers/statistics'),
  getTop: (limit = 10) => api.get(`/mobile/customers/top?limit=${limit}`),
  getInactive: (days = 60) => api.get(`/mobile/customers/inactive?days=${days}`)
};

/**
 * Invoices API
 */
export const invoicesAPI = {
  getAll: () => api.get('/mobile/invoices'),
  getById: (id) => api.get(`/mobile/invoices/${id}`),
  create: (data) => api.post('/mobile/invoices', data),
  update: (id, data) => api.put(`/mobile/invoices/${id}`, data),
  delete: (id) => api.delete(`/mobile/invoices/${id}`),
  search: (query) => api.get(`/mobile/invoices/search?query=${encodeURIComponent(query)}`),
  getByCustomer: (customerId) => api.get(`/mobile/invoices/customer/${customerId}`),
  addPayment: (id, paymentData) => api.post(`/mobile/invoices/${id}/payments`, paymentData),
  getPayments: (id) => api.get(`/mobile/invoices/${id}/payments`)
};

/**
 * Receipts API (سندات القبض)
 */
export const receiptsAPI = {
  getAll: () => api.get('/mobile/receipts'),
  getById: (id) => api.get(`/mobile/receipts/${id}`),
  create: (data) => api.post('/mobile/receipts', data),
  update: (id, data) => api.put(`/mobile/receipts/${id}`, data),
  delete: (id) => api.delete(`/mobile/receipts/${id}`)
};

/**
 * Payment Methods API
 */
export const paymentMethodsAPI = {
  getAll: () => api.get('/mobile/payment-methods')
};

/**
 * Collections API
 */
export const collectionsAPI = {
  getAll: () => api.get('/mobile/collections'),
  getById: (id) => api.get(`/mobile/collections/${id}`),
  create: (data) => api.post('/mobile/collections', data),
  update: (id, data) => api.put(`/mobile/collections/${id}`, data),
  delete: (id) => api.delete(`/mobile/collections/${id}`),
  search: (query) => api.get(`/mobile/collections/search?query=${encodeURIComponent(query)}`),
  getByCustomer: (customerId) => api.get(`/mobile/collections/customer/${customerId}`)
};

/**
 * Returns API (Stock Returns from Representative)
 */
export const returnsAPI = {
  getAll: () => api.get('/mobile/returns'),
  getById: (id) => api.get(`/mobile/returns/${id}`),
  create: (data) => api.post('/mobile/returns', data), // Create stock return request
  delete: (id) => api.delete(`/mobile/returns/${id}`), // Delete pending return (only if status = pending)
  search: (query) => api.get(`/mobile/returns/search?query=${encodeURIComponent(query)}`)
};

/**
 * Dashboard API
 */
export const dashboardAPI = {
  getStats: () => api.get('/mobile/dashboard/stats'),
  getSalesChart: (days = 7) => api.get(`/mobile/dashboard/sales-chart?days=${days}`),
  getRecentActivity: (limit = 10) => api.get(`/mobile/dashboard/recent-activity?limit=${limit}`),
  getRecentInvoices: (limit = 5) => api.get(`/mobile/dashboard/recent-invoices?limit=${limit}`),
  getTopProducts: (limit = 5) => api.get(`/mobile/dashboard/top-products?limit=${limit}`),
  getTopCustomers: (limit = 5) => api.get(`/mobile/dashboard/top-customers?limit=${limit}`)
};

/**
 * Auth API
 */
export const authAPI = {
  login: (email, password) => api.post('/mobile/auth/login', { email, password }),
  logout: () => api.post('/mobile/auth/logout'),
  getProfile: () => api.get('/mobile/auth/profile'),
  updateProfile: (data) => api.put('/mobile/auth/profile', data),
  changePassword: (data) => api.post('/mobile/auth/change-password', data)
};

/**
 * Representative Vehicle Inventory API (NEW)
 * Track products in representative's vehicle
 */
export const myInventoryAPI = {
  getAll: () => api.get('/mobile/my-inventory'),
  getProduct: (productId) => api.get(`/mobile/my-inventory/${productId}`)
};

/**
 * Settings API
 */
export const settingsAPI = {
  getAll: () => api.get('/mobile/settings'),
  getTaxRate: () => api.get('/mobile/settings').then(res => res.data?.data?.general?.tax_rate || 0.14)
};

/**
 * Helper: Check if online
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Helper: Save JWT Token
 */
export const saveToken = (token) => {
  localStorage.setItem('delivery_access_token', token);
};

/**
 * Helper: Get JWT Token
 */
export const getToken = () => {
  return localStorage.getItem('delivery_access_token');
};

/**
 * Helper: Remove JWT Token
 */
export const removeToken = () => {
  localStorage.removeItem('delivery_access_token');
};

export default api;
